import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, unref as _unref, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["onKeydown"]
const _hoisted_2 = {
  key: 0,
  class: "patient-row__other-indicator"
}
const _hoisted_3 = { class: "patient-row__column patient-row__column--status" }
const _hoisted_4 = { class: "patient-row__status-details" }
const _hoisted_5 = {
  key: 0,
  class: "patient-row__discharge-time"
}
const _hoisted_6 = {
  key: 1,
  class: "patient-row__discharge-time"
}
const _hoisted_7 = { class: "patient-row__status-row" }
const _hoisted_8 = {
  key: 0,
  class: "patient-row__overdue"
}
const _hoisted_9 = { class: "patient-row__overdue-text" }
const _hoisted_10 = {
  key: 1,
  class: "patient-row__separator"
}
const _hoisted_11 = { class: "patient-row__column patient-row__column--diagnosis" }
const _hoisted_12 = { class: "patient-row__diagnosis-text" }
const _hoisted_13 = { class: "patient-row__column patient-row__column--room" }
const _hoisted_14 = { class: "patient-row__room-info" }
const _hoisted_15 = { class: "patient-row__room-text" }
const _hoisted_16 = { class: "patient-row__actions" }
const _hoisted_17 = { class: "patient-row__expanded" }
const _hoisted_18 = { class: "patient-row__expanded-content" }
const _hoisted_19 = { class: "patient-row__details" }
const _hoisted_20 = { class: "patient-row__detail-item" }
const _hoisted_21 = { class: "patient-row__detail-value" }
const _hoisted_22 = { class: "patient-row__detail-item" }
const _hoisted_23 = { class: "patient-row__detail-value" }
const _hoisted_24 = { class: "patient-row__detail-item" }
const _hoisted_25 = { class: "patient-row__detail-value" }
const _hoisted_26 = { class: "patient-row__detail-item" }
const _hoisted_27 = { class: "patient-row__detail-value" }
const _hoisted_28 = { class: "patient-row__detail-item patient-row__detail-item--gender" }
const _hoisted_29 = { class: "patient-row__gender" }
const _hoisted_30 = { class: "patient-row__detail-value" }
const _hoisted_31 = { class: "patient-row__risks" }
const _hoisted_32 = {
  key: 0,
  class: "patient-row__risk-badges"
}
const _hoisted_33 = { class: "patient-row__call-history-section" }
const _hoisted_34 = {
  key: 1,
  class: "patient-row__call-history-empty"
}
const _hoisted_35 = {
  key: 2,
  class: "patient-row__call-history-list"
}
const _hoisted_36 = { class: "patient-row__call-history-main" }
const _hoisted_37 = {
  key: 1,
  class: "patient-row__call-history-nurse"
}
const _hoisted_38 = { class: "patient-row__call-history-time" }
const _hoisted_39 = {
  key: 2,
  class: "patient-row__call-history-duration"
}
const _hoisted_40 = {
  key: 0,
  class: "patient-row__call-history-notes"
}
const _hoisted_41 = { class: "patient-row__notes-text" }

import { computed, ref, watch } from "vue";
import type { Patient, CallLog } from "@virtual-care/shared";
import { formatTimeAgo, formatOverdueTime as formatOverdueTimeUtil, formatElapsedTime } from "../utils/timeFormat";
import { callLogService } from "../services/callLogService";
import { usePatientStore } from "../store/patientStore";
import NotesModal from "./NotesModal.vue";

interface Props {
  patient: Patient;
}

interface Emits {
  (e: "call", patientId: string): void;
  (e: "breakthrough", patientId: string): void;
  (e: "expand", patientId: string): void;
}

const userId = "nurse-001"; // TODO: Get from auth context

const OVERDUE_THRESHOLD_HOURS = 6;

export default /*@__PURE__*/_defineComponent({
  __name: 'PatientRow',
  props: {
    patient: {}
  },
  emits: ["call", "breakthrough", "expand"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const patientStore = usePatientStore();

const isExpanded = ref(false);
const callHistory = ref<CallLog[]>([]);
const callHistoryLoading = ref(false);

// Edit notes modal state
const showEditNotesModal = ref(false);
const editingCallLog = ref<CallLog | null>(null);
watch(isExpanded, async (expanded) => {
  if (!expanded || callHistory.value.length > 0) return;
  callHistoryLoading.value = true;
  try {
    callHistory.value = await callLogService.getCallLogsByPatient(props.patient.patientId, 10);
  } catch {
    callHistory.value = [];
  } finally {
    callHistoryLoading.value = false;
  }
});

const getCallHistoryOutcomeColor = (log: CallLog): string => {
  if (log.status === "initiated" || log.status === "accepted") return "#795de9";
  if (log.endReason === "normal") return "#25883b";
  if (log.endReason === "declined") return "#d73d17";
  return "#7f8287";
};

const getCallHistoryOutcomeLabel = (log: CallLog): string => {
  if (log.status === "initiated") return "Calling...";
  if (log.status === "accepted") return "In progress";
  if (log.endReason === "cancelled") return "Cancelled";
  if (log.endReason === "timeout" || log.status === "failed") return "No answer";
  if (log.endReason === "declined") return "Declined";
  if (log.endReason === "normal") return "Completed";
  return "Unknown";
};

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// Notes editing functions
const canEditNotes = (log: CallLog): boolean => {
  if (!log.notesEditedAt) return false;
  const editedAt = new Date(log.notesEditedAt);
  const now = new Date();
  const minutesSince = (now.getTime() - editedAt.getTime()) / (1000 * 60);
  return minutesSince <= 15;
};

const handleEditNotes = (log: CallLog) => {
  // Reuse the same NotesModal component for editing
  editingCallLog.value = log;
  showEditNotesModal.value = true;
};

const handleEditNotesSaved = async (notes: string) => {
  if (!editingCallLog.value) return;
  
  try {
    await callLogService.updateCallLog(editingCallLog.value.callId, {
      notes,
      notesEditedAt: new Date().toISOString(),
      notesEditedBy: userId,
    });
    
    showEditNotesModal.value = false;
    editingCallLog.value = null;
    
    // Refresh call history to show updated notes
    callHistoryLoading.value = true;
    callHistory.value = await callLogService.getCallLogsByPatient(props.patient.patientId, 10);
    
    // Also refresh the patient's recent call log in the store
    await patientStore.refreshPatientCallLog(props.patient.patientId);
  } catch (err) {
    console.error('[PatientRow] Failed to update notes:', err);
  } finally {
    callHistoryLoading.value = false;
  }
};

const PRIMARY_CATEGORIES = ["discharge-ready", "condition-discharge", "new-admit"];

const isOtherCategory = computed(() => {
  return !PRIMARY_CATEGORIES.includes(props.patient.category);
});

const isOverdueLocal = computed(() => {
  const now = Date.now();
  const recentLog = props.patient.recentCallLog;
  
  // For Discharge Ready and Conditional Discharge patients
  if (props.patient.dischargeOrderTime) {
    const orderTime = new Date(props.patient.dischargeOrderTime).getTime();
    const hoursSinceOrder = (now - orderTime) / (1000 * 60 * 60);
    
    // Not overdue if less than threshold
    if (hoursSinceOrder <= OVERDUE_THRESHOLD_HOURS) return false;
    
    // Check if there's been a successful completed call since discharge order
    if (recentLog && recentLog.status === 'completed') {
      const callTime = new Date(recentLog.endTime || recentLog.startTime).getTime();
      // If the completed call happened AFTER the discharge order, patient is no longer overdue
      if (callTime > orderTime) {
        return false;
      }
    }
    
    return true;
  }
  
  // For New Admit patients - check admission time
  if (props.patient.status === 'new-admit' && props.patient.admissionDateTime) {
    const admissionTime = new Date(props.patient.admissionDateTime).getTime();
    const hoursSinceAdmission = (now - admissionTime) / (1000 * 60 * 60);
    
    // Not overdue if less than threshold
    if (hoursSinceAdmission <= OVERDUE_THRESHOLD_HOURS) return false;
    
    // Check if there's been a successful completed call since admission
    if (recentLog && recentLog.status === 'completed') {
      const callTime = new Date(recentLog.endTime || recentLog.startTime).getTime();
      // If the completed call happened AFTER admission, patient is no longer overdue
      if (callTime > admissionTime) {
        return false;
      }
    }
    
    return true;
  }
  
  return false;
});

const borderColor = computed(() => {
  if (isOverdueLocal.value) {
    return "#D73D17";
  }
  return "transparent";
});

const statusBadgeColor = computed(() => {
  if (props.patient.status === "discharge-ready") {
    return isOverdueLocal.value ? "#D73D17" : "#EEF5EF";
  } else if (props.patient.status === "condition-discharge") {
    return isOverdueLocal.value ? "#D73D17" : "#FEF3E8";
  } else if (props.patient.status === "new-admit") {
    return isOverdueLocal.value ? "#D73D17" : "#E8EEFB";
  } else if (isOtherCategory.value) {
    return "#F5F5F5";
  }
  return "#D73D17";
});

const statusBadgeTextColor = computed(() => {
  if (props.patient.status === "discharge-ready") {
    return isOverdueLocal.value ? "#FFFFFF" : "#222222";
  } else if (props.patient.status === "condition-discharge") {
    return isOverdueLocal.value ? "#FFFFFF" : "#222222";
  } else if (props.patient.status === "new-admit") {
    return isOverdueLocal.value ? "#FFFFFF" : "#222222";
  } else if (isOtherCategory.value) {
    return "#222222";
  }
  return "#FFFFFF";
});

const bedsideNurseName = computed(() => {
  const bedsideNurse = props.patient.careTeamMembers.find((member) => member.isBedside);
  return bedsideNurse?.name || null;
});

const dischargeOrderLabel = computed(() => {
  return props.patient.status === "condition-discharge" ? "Conditional trigger" : "Discharge order";
});

const displayReason = computed(() => {
  return props.patient.status === "condition-discharge" ? "Conditional Discharge" : props.patient.reason;
});

const formatDischargeTime = computed(() => {
  return formatElapsedTime(props.patient.dischargeOrderTime);
});

const formatAdmissionTime = computed(() => {
  return formatElapsedTime(props.patient.admissionDateTime);
});

const formatOverdueTime = computed(() => {
  // For discharge ready patients, use discharge order time
  if (props.patient.dischargeOrderTime) {
    return formatOverdueTimeUtil(props.patient.dischargeOrderTime);
  }
  // For new admit patients, use admission time
  if (props.patient.status === 'new-admit' && props.patient.admissionDateTime) {
    return formatOverdueTimeUtil(props.patient.admissionDateTime);
  }
  return "";
});

const callLogDisplay = computed(() => {
  const recentLog = props.patient.recentCallLog;
  const totalCount = props.patient.totalCallCount || 0;

  // Rule 1: No call log present
  if (!recentLog) {
    const thresholdMinutes = OVERDUE_THRESHOLD_HOURS * 60;
    return {
      text: `Target response within ${thresholdMinutes} minutes`,
      type: "default",
    };
  }

  // Rule 4: Call in progress (patient answered)
  if (recentLog.status === "accepted") {
    return {
      text: "In progress…",
      type: "in-progress",
    };
  }

  // Rule 5: Call initiated but not yet answered
  if (recentLog.status === "initiated") {
    return {
      text: "Calling…",
      type: "calling",
    };
  }

  const timeAgo = formatTimeAgo(recentLog.updatedAt);

  // Rule 2: Cancelled by nurse
  if (recentLog.endReason === "cancelled") {
    return {
      text: `Cancelled • ${timeAgo}`,
      type: "cancelled",
    };
  }

  // Rule 3: Not answered or timeout
  if (recentLog.endReason === "timeout" || recentLog.status === "failed") {
    return {
      text: `Attempt ${totalCount} • ${timeAgo} • No answer`,
      type: "no-answer",
    };
  }

  // Rule 4: Declined
  if (recentLog.endReason === "declined") {
    return {
      text: `Declined call • ${timeAgo}`,
      type: "declined",
    };
  }

  // Rule 5: Normal completion
  if (recentLog.endReason === "normal") {
    return {
      text: `Completed • ${timeAgo}`,
      type: "completed",
    };
  }

  // Fallback
  return {
    text: `Last call • ${timeAgo}`,
    type: "default",
  };
});

// Availability response badge
const hasAvailabilityResponse = computed(() => {
  const hasResponse = !!props.patient.availabilityResponse;
  if (hasResponse) {
    console.log('[PatientRow] Patient has availability response:', props.patient.patientId, props.patient.availabilityResponse);
  }
  return hasResponse;
});

const availabilityResponseType = computed(() => {
  return props.patient.availabilityResponse?.response || 'not_now';
});

const availabilityBadgeIcon = computed(() => {
  return props.patient.availabilityResponse?.response === 'available' 
    ? 'mdi-check-circle' 
    : 'mdi-clock-outline';
});

const availabilityBadgeColor = computed(() => {
  return props.patient.availabilityResponse?.response === 'available' 
    ? '#25883B' 
    : '#D79D17';
});

const availabilityBadgeText = computed(() => {
  return props.patient.availabilityResponse?.response === 'available' 
    ? 'Available now' 
    : 'Will call back';
});

const formatAdmittedDate = computed(() => {
  if (!props.patient.admissionDateTime) return "N/A";
  const date = new Date(props.patient.admissionDateTime);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${day}-${month}-${year}, ${time}`;
});

const formatDischargeOrderDate = computed(() => {
  if (!props.patient.dischargeOrderTime) return "N/A";
  const date = new Date(props.patient.dischargeOrderTime);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${day}-${month}-${year} ${time}`;
});

const formatDateOfBirth = computed(() => {
  if (!props.patient.dateOfBirth) return "N/A";
  const dob = new Date(props.patient.dateOfBirth);
  const now = new Date();

  const ageYears = now.getFullYear() - dob.getFullYear();
  const ageMonths = now.getMonth() - dob.getMonth();

  const day = dob.getDate();
  const month = dob.toLocaleDateString("en-US", { month: "short" });
  const year = dob.getFullYear();

  return `${day}-${month}-${year} (${ageYears}y ${Math.abs(ageMonths)}m)`;
});

const getRiskIcon = (flag: string): string => {
  const flagLower = flag.toLowerCase();
  if (flagLower.includes("fall")) return "mdi-human-cane";
  if (flagLower.includes("isolation")) return "mdi-biohazard";
  if (flagLower.includes("safety")) return "mdi-alert";
  if (flagLower.includes("elopement")) return "mdi-exit-run";
  if (flagLower.includes("seizure")) return "mdi-brain";
  if (flagLower.includes("aspiration") || flagLower.includes("npo")) return "mdi-cup-off";
  if (flagLower.includes("allergy") || flagLower.includes("latex")) return "mdi-allergy";
  if (flagLower.includes("contact") || flagLower.includes("droplet") || flagLower.includes("airborne"))
    return "mdi-shield-alert";
  return "mdi-information";
};

const getRiskBadgeClass = (flag: string): string => {
  const flagLower = flag.toLowerCase();
  if (flagLower.includes("fall")) return "patient-row__risk-badge--fall";
  if (flagLower.includes("isolation")) return "patient-row__risk-badge--isolation";
  if (flagLower.includes("safety")) return "patient-row__risk-badge--safety";
  return "";
};

const genderColor = computed(() => {
  const gender = props.patient.gender || "M";
  return gender === "M" ? "#5DA7B5" : "#BC7AA9";
});

const handleCall = () => {
  emit("call", props.patient.patientId);
};

const handleBreakthrough = () => {
  emit("breakthrough", props.patient.patientId);
};

const handleExpand = () => {
  isExpanded.value = !isExpanded.value;
  emit("expand", props.patient.patientId);
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_VSkeletonParagraph = _resolveComponent("VSkeletonParagraph")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['patient-row', { 'patient-row--overdue': isOverdueLocal.value, 'patient-row--expanded': isExpanded.value }]),
    style: _normalizeStyle({ borderLeftColor: borderColor.value })
  }, [
    _createElementVNode("div", {
      class: "patient-row__content",
      role: "button",
      tabindex: "0",
      onClick: handleExpand,
      onKeydown: [
        _withKeys(handleExpand, ["enter"]),
        _withKeys(_withModifiers(handleExpand, ["prevent"]), ["space"])
      ]
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["patient-row__column patient-row__column--reason", { 'patient-row__column--center': isOtherCategory.value }])
      }, [
        (isOtherCategory.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, "--"))
          : (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: "patient-row__status-badge",
              style: _normalizeStyle({ backgroundColor: statusBadgeColor.value, color: statusBadgeTextColor.value })
            }, _toDisplayString(displayReason.value), 5))
      ], 2),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          (__props.patient.dischargeOrderTime)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(dischargeOrderLabel.value) + ": " + _toDisplayString(formatDischargeTime.value), 1))
            : _createCommentVNode("", true),
          (__props.patient.status === 'new-admit' && __props.patient.admissionDateTime)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, " Admitted: " + _toDisplayString(formatAdmissionTime.value), 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_7, [
            (isOverdueLocal.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(_component_v_icon, {
                    size: "16",
                    color: "#D73D17"
                  }, {
                    default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                      _createTextVNode("mdi-alert-circle", -1)
                    ]))]),
                    _: 1
                  }),
                  _createElementVNode("span", _hoisted_9, _toDisplayString(formatOverdueTime.value) + " overdue", 1)
                ]))
              : _createCommentVNode("", true),
            (isOverdueLocal.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_10, "|"))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: _normalizeClass(["patient-row__call-log", `patient-row__call-log--${callLogDisplay.value.type}`])
            }, _toDisplayString(callLogDisplay.value.text), 3),
            (hasAvailabilityResponse.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 2,
                  class: _normalizeClass(["patient-row__availability-badge", `patient-row__availability-badge--${availabilityResponseType.value}`])
                }, [
                  _createVNode(_component_v_icon, {
                    size: "14",
                    color: availabilityBadgeColor.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(availabilityBadgeIcon.value), 1)
                    ]),
                    _: 1
                  }, 8, ["color"]),
                  _createElementVNode("span", null, _toDisplayString(availabilityBadgeText.value), 1)
                ], 2))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("div", _hoisted_12, _toDisplayString(__props.patient.primaryDiagnosis), 1)
      ]),
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("div", _hoisted_14, [
          _createVNode(_component_v_icon, {
            size: "16",
            color: "#D9D9D9"
          }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode("mdi-hospital-box", -1)
            ]))]),
            _: 1
          }),
          _createElementVNode("span", _hoisted_15, _toDisplayString(__props.patient.room.roomNumber) + " / Bed " + _toDisplayString(__props.patient.bed.bedNumber) + " / " + _toDisplayString(__props.patient.patientName), 1)
        ])
      ]),
      _createElementVNode("div", _hoisted_16, [
        _createVNode(_component_v_btn, {
          class: "patient-row__call-btn",
          variant: "flat",
          size: "small",
          rounded: "pill",
          onClick: _withModifiers(handleCall, ["stop"])
        }, {
          default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
            _createTextVNode(" Call ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_component_v_btn, {
          class: "patient-row__breakthrough-btn",
          variant: "outlined",
          size: "small",
          rounded: "pill",
          onClick: _withModifiers(handleBreakthrough, ["stop"])
        }, {
          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createTextVNode(" Breakthrough ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_component_v_btn, {
          icon: "",
          variant: "text",
          size: "small",
          class: "patient-row__expand-btn",
          onClick: _withModifiers(handleExpand, ["stop"])
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, null, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(isExpanded.value ? "mdi-chevron-up" : "mdi-chevron-down"), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ], 40, _hoisted_1),
    _createVNode(_component_v_expand_transition, null, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("div", _hoisted_19, [
              _createElementVNode("div", _hoisted_20, [
                _cache[5] || (_cache[5] = _createElementVNode("span", { class: "patient-row__detail-label" }, "Admitted", -1)),
                _createElementVNode("span", _hoisted_21, _toDisplayString(formatAdmittedDate.value), 1)
              ]),
              _createElementVNode("div", _hoisted_22, [
                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "patient-row__detail-label" }, "Discharge Order", -1)),
                _createElementVNode("span", _hoisted_23, _toDisplayString(formatDischargeOrderDate.value), 1)
              ]),
              _createElementVNode("div", _hoisted_24, [
                _cache[7] || (_cache[7] = _createElementVNode("span", { class: "patient-row__detail-label" }, "Bedside Nurse", -1)),
                _createElementVNode("span", _hoisted_25, _toDisplayString(bedsideNurseName.value || "Not assigned"), 1)
              ]),
              _createElementVNode("div", _hoisted_26, [
                _cache[8] || (_cache[8] = _createElementVNode("span", { class: "patient-row__detail-label" }, "DOB", -1)),
                _createElementVNode("span", _hoisted_27, _toDisplayString(formatDateOfBirth.value), 1)
              ]),
              _createElementVNode("div", _hoisted_28, [
                _cache[9] || (_cache[9] = _createElementVNode("span", { class: "patient-row__detail-label" }, "Gender", -1)),
                _createElementVNode("div", _hoisted_29, [
                  _createElementVNode("div", {
                    class: "patient-row__gender-badge",
                    style: _normalizeStyle({ backgroundColor: genderColor.value })
                  }, null, 4),
                  _createElementVNode("span", _hoisted_30, _toDisplayString(__props.patient.gender || "M"), 1)
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_31, [
              (__props.patient.patientFlags.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.patient.patientFlags, (flag, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: index,
                        class: _normalizeClass(["patient-row__risk-badge", getRiskBadgeClass(flag)])
                      }, [
                        _createVNode(_component_v_icon, {
                          size: "24",
                          color: "#291758"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(getRiskIcon(flag)), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createElementVNode("span", null, _toDisplayString(flag), 1)
                      ], 2))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              _createVNode(_component_v_btn, {
                icon: "",
                variant: "text",
                size: "small",
                class: "patient-row__chart-btn"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, {
                    size: "24",
                    color: "#222222"
                  }, {
                    default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                      _createTextVNode("mdi-clipboard-pulse-outline", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ]),
          _createElementVNode("div", _hoisted_33, [
            _cache[15] || (_cache[15] = _createElementVNode("div", { class: "patient-row__call-history-title" }, "CALL HISTORY", -1)),
            (callHistoryLoading.value)
              ? (_openBlock(), _createBlock(_component_VSkeletonParagraph, {
                  key: 0,
                  class: "patient-row__call-history-skeleton"
                }))
              : (callHistory.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_34, " No call history "))
                : (_openBlock(), _createElementBlock("div", _hoisted_35, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(callHistory.value, (log) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: log.callId,
                        class: "patient-row__call-history-item"
                      }, [
                        _createElementVNode("div", _hoisted_36, [
                          _createVNode(_component_v_chip, {
                            size: "x-small",
                            style: _normalizeStyle({ 
                    backgroundColor: getCallHistoryOutcomeColor(log) + ' !important', 
                    color: '#ffffff !important',
                    opacity: '1 !important'
                  }),
                            class: "patient-row__call-history-tag"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(getCallHistoryOutcomeLabel(log)), 1)
                            ]),
                            _: 2
                          }, 1032, ["style"]),
                          (log.isBreakthrough)
                            ? (_openBlock(), _createBlock(_component_v_chip, {
                                key: 0,
                                size: "x-small",
                                style: { 
                    backgroundColor: '#D79D17 !important', 
                    color: '#ffffff !important',
                    opacity: '1 !important'
                  },
                                class: "patient-row__breakthrough-chip"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, {
                                    start: "",
                                    size: "12"
                                  }, {
                                    default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                      _createTextVNode("mdi-alert-circle", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _cache[12] || (_cache[12] = _createTextVNode(" Breakthrough ", -1)),
                                  (log.breakthroughReason)
                                    ? (_openBlock(), _createBlock(_component_v_tooltip, {
                                        key: 0,
                                        activator: "parent",
                                        location: "top"
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(log.breakthroughReason), 1)
                                        ]),
                                        _: 2
                                      }, 1024))
                                    : _createCommentVNode("", true)
                                ]),
                                _: 2
                              }, 1024))
                            : _createCommentVNode("", true),
                          (log.initiatorName)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_37, _toDisplayString(log.initiatorName), 1))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_38, _toDisplayString(_unref(formatTimeAgo)(log.updatedAt)), 1),
                          (log.duration)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_39, _toDisplayString(formatDuration(log.duration)), 1))
                            : _createCommentVNode("", true)
                        ]),
                        (log.notes)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_40, [
                              _createVNode(_component_v_icon, {
                                size: "14",
                                color: "#6A5D8A"
                              }, {
                                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                  _createTextVNode("mdi-note-text", -1)
                                ]))]),
                                _: 1
                              }),
                              _createElementVNode("span", _hoisted_41, _toDisplayString(log.notes), 1),
                              (canEditNotes(log))
                                ? (_openBlock(), _createBlock(_component_v_btn, {
                                    key: 0,
                                    icon: "",
                                    size: "x-small",
                                    variant: "text",
                                    onClick: ($event: any) => (handleEditNotes(log))
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_icon, { size: "14" }, {
                                        default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                          _createTextVNode("mdi-pencil", -1)
                                        ]))]),
                                        _: 1
                                      })
                                    ]),
                                    _: 1
                                  }, 8, ["onClick"]))
                                : _createCommentVNode("", true)
                            ]))
                          : _createCommentVNode("", true)
                      ]))
                    }), 128))
                  ]))
          ])
        ], 512), [
          [_vShow, isExpanded.value]
        ])
      ]),
      _: 1
    }),
    _createVNode(NotesModal, {
      modelValue: showEditNotesModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showEditNotesModal).value = $event)),
      "call-was-accepted": true,
      "session-id": editingCallLog.value?.sessionId || '',
      "existing-notes": editingCallLog.value?.notes || '',
      "patient-name": __props.patient.patientName,
      onNotesSaved: handleEditNotesSaved
    }, null, 8, ["modelValue", "session-id", "existing-notes", "patient-name"])
  ], 6))
}
}

})